import React, { createContext, useContext, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import axios from "../axios";
import endpoint from "../endpoints";

import { isOwnerOrAdmin, hasAnyPermission, getDefaultRoute, getUser } from "../utils/auth";

export const SubscriptionContext = createContext({ daysLeft: null, isExpired: false });
export const useSubscription = () => useContext(SubscriptionContext);

export const RequireAuth = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

export const RedirectIfAuthed = ({ children }) => {
  const token = localStorage.getItem("token");
  if (token) return <Navigate to={getDefaultRoute()} replace />;
  return children;
};

/**
 * Route level permission guard.
 * If user lacks required permission or role, gracefully redirects to their default accessible route.
 */
export const RequirePermission = ({ permissions = [], roles = [], children }) => {
  const user = getUser();
  if (!user) return <Navigate to="/login" replace />;
  if (isOwnerOrAdmin()) return children;

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  if (permissions.length > 0 && !hasAnyPermission(permissions)) {
    return <Navigate to={getDefaultRoute()} replace />;
  }

  return children;
};

// Wraps all authenticated app routes — fetches quota once, redirects to /renew
// if expired, otherwise provides daysLeft via context for the banner.
export const SubscriptionGate = () => {
  const location = useLocation();
  const [state, setState] = useState({ daysLeft: null, isExpired: false, loaded: false });

  useEffect(() => {
    // Fetch only once on mount — not on every route change
    axios.get(endpoint.QUOTA).then((res) => {
      const data = res.data?.data;
      if (!data) { setState({ daysLeft: null, isExpired: false, loaded: true }); return; }

      const isExpired = !data.active_subscription;
      let daysLeft = null;

      if (data.ends_at) {
        const diff = Math.ceil((new Date(data.ends_at) - Date.now()) / 86400000);
        daysLeft = diff > 0 ? diff : 0;
      }

      setState({ daysLeft, isExpired, loaded: true });
    }).catch(() => {
      setState({ daysLeft: null, isExpired: false, loaded: true });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Expired — force to /renew unless already there
  if (state.loaded && state.isExpired && location.pathname !== "/renew") {
    return <Navigate to="/renew" replace />;
  }

  // Never return null — always render Outlet so AppLayout stays mounted
  return (
    <SubscriptionContext.Provider value={{ daysLeft: state.daysLeft, isExpired: state.isExpired }}>
      <Outlet />
    </SubscriptionContext.Provider>
  );
};
